import React, { useRef, useEffect, useCallback } from 'react';

const SPRING = 0.042;
const DAMPING = 0.87;
const MOUSE_RADIUS = 165;
const REPULSION = 5.5;
const CONN_DIST = 115;

const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    particles: [],
    bgParticles: [],
    mouse: { x: -2000, y: -2000, active: false },
    frameId: 0,
  });

  const init = useCallback((w, h) => {
    const isMobile = w < 768;
    const count = isMobile ? 60 : 130;
    const state = stateRef.current;

    state.particles = Array.from({ length: count }, () => {
      const x = Math.random() * w;
      const y = Math.random() * h;
      return {
        x, y, originX: x, originY: y,
        vx: 0, vy: 0,
        size: Math.random() * 1.4 + 0.7,
        isCyan: Math.random() > 0.80,
        activation: 0,
      };
    });

    state.bgParticles = Array.from({ length: isMobile ? 40 : 80 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.12,
      vy: (Math.random() - 0.5) * 0.12,
      size: Math.random() * 0.9 + 0.25,
      phase: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = stateRef.current;
    let alive = true;

    const getLogical = () => ({
      w: canvas.width / (window.devicePixelRatio || 1),
      h: canvas.height / (window.devicePixelRatio || 1),
    });

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);
      init(w, h);
    };

    const animate = (time) => {
      if (!alive) return;
      const { w, h } = getLogical();
      ctx.clearRect(0, 0, w, h);

      // Pulsating radial glow from center
      const cx = w / 2;
      const cy = h * 0.45;
      const pulse = Math.sin(time * 0.00065) * 0.038 + 0.072;
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.68);
      grd.addColorStop(0, `rgba(0, 212, 255, ${pulse})`);
      grd.addColorStop(0.5, `rgba(0, 100, 180, ${pulse * 0.3})`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);

      // Ambient twinkling stars
      for (const p of state.bgParticles) {
        p.x = (p.x + p.vx + w) % w;
        p.y = (p.y + p.vy + h) % h;
        const twinkle = Math.sin(time * 0.0017 + p.phase) * 0.5 + 0.5;
        ctx.globalAlpha = 0.04 + 0.14 * twinkle;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const { particles, mouse } = state;

      // Physics: spring + mouse repulsion
      for (const p of particles) {
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const distSq = dx * dx + dy * dy;
        const dist = Math.sqrt(distSq);

        if (mouse.active && dist < MOUSE_RADIUS && dist > 0.5) {
          const force = ((MOUSE_RADIUS - dist) / MOUSE_RADIUS) * REPULSION;
          p.vx -= (dx / dist) * force;
          p.vy -= (dy / dist) * force;
          p.activation = Math.min(1, p.activation + 0.20);
        } else {
          p.activation *= 0.94;
        }

        p.vx += (p.originX - p.x) * SPRING;
        p.vy += (p.originY - p.y) * SPRING;
        p.vx *= DAMPING;
        p.vy *= DAMPING;
        p.x += p.vx;
        p.y += p.vy;
      }

      // Neural network connections
      ctx.lineWidth = 0.55;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          if (Math.abs(dx) > CONN_DIST || Math.abs(dy) > CONN_DIST) continue;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d >= CONN_DIST) continue;
          const prox = 1 - d / CONN_DIST;
          const act = Math.max(p1.activation, p2.activation);
          const alpha = prox * 0.09 + act * prox * 0.38;
          ctx.strokeStyle = `rgba(0, ${Math.floor(185 + 27 * prox)}, 255, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }

      // Draw particles
      for (const p of particles) {
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        const opacity = Math.min(0.20 + p.activation * 0.58 + speed * 0.05, 1);
        const radius = p.size * (1 + p.activation * 0.55);

        if (p.activation > 0.06) {
          ctx.shadowBlur = 8 * p.activation;
          ctx.shadowColor = '#00D4FF';
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = p.isCyan
          ? `rgba(0, 212, 255, ${opacity})`
          : `rgba(192, 218, 255, ${opacity * 0.60})`;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      state.frameId = requestAnimationFrame(animate);
    };

    const onMove = (e) => {
      state.mouse.x = e.clientX;
      state.mouse.y = e.clientY;
      state.mouse.active = true;
    };
    const onLeave = () => { state.mouse.active = false; };
    const onVisibility = () => {
      if (!document.hidden && alive) state.frameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    resize();
    state.frameId = requestAnimationFrame(animate);

    return () => {
      alive = false;
      cancelAnimationFrame(state.frameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
};

export default ParticleCanvas;
